/**
 * AdminA2Bitacora.jsx · v2.8.9.1
 * ────────────────────────────────
 * Panel de Bitácora del Motor A2: tabla de eventos de auto-agendamiento
 * con auto-refresh, filtros por técnico/canal y KPIs del día.
 */
import { useEffect, useState, useMemo, useCallback } from "react";
import Layout from "../components/Layout";
import A2AutopilotSwitch from "../components/A2AutopilotSwitch";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import {
  Bot, RefreshCw, Loader2, Search, Zap, User, Clock, MessageSquare,
  CheckCircle2, Download,
} from "lucide-react";

const REFRESH_MS = 15000;

export default function AdminA2Bitacora() {
  const { hasPerm } = useAuth();
  const canView = hasPerm ? hasPerm("M32.a2.view") : false;

  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [q, setQ] = useState("");
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastFetchAt, setLastFetchAt] = useState(null);

  const fetchEvents = useCallback(async (silent = false) => {
    if (!silent) setRefreshing(true);
    try {
      const { data } = await api.get("/a2/autopilot/events", { params: { limit: 200 } });
      setEvents(data?.items || []);
      setLastFetchAt(new Date());
    } catch (e) {
      if (!silent) toast.error(e?.response?.data?.detail || "No se pudo cargar la bitácora");
    } finally {
      setLoading(false);
      if (!silent) setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    if (!canView) { setLoading(false); return; }
    fetchEvents();
  }, [canView, fetchEvents]);

  useEffect(() => {
    if (!autoRefresh || !canView) return;
    const iv = setInterval(() => fetchEvents(true), REFRESH_MS);
    return () => clearInterval(iv);
  }, [autoRefresh, canView, fetchEvents]);

  const filtered = useMemo(() => {
    if (!q) return events;
    const s = q.toLowerCase();
    return events.filter((e) =>
      (e.tech_email || "").toLowerCase().includes(s) ||
      String(e.odoo_task_id || "").includes(s) ||
      (e.notes || "").toLowerCase().includes(s) ||
      (e.channel || "").toLowerCase().includes(s)
    );
  }, [events, q]);

  const kpis = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    const todayEvents = events.filter((e) => (e.created_at || "").slice(0, 10) === today);
    const uniqueTechs = new Set(todayEvents.map((e) => e.tech_email).filter(Boolean));
    return {
      total: events.length,
      today: todayEvents.length,
      techs_today: uniqueTechs.size,
      last_hour: events.filter((e) => {
        try {
          const at = new Date(e.created_at);
          return (Date.now() - at.getTime()) < 3600000;
        } catch { return false; }
      }).length,
    };
  }, [events]);

  const exportCSV = () => {
    if (!filtered.length) { toast.error("No hay eventos para exportar"); return; }
    const cols = ["created_at", "odoo_task_id", "event", "tech_email", "slot_start", "slot_end", "channel", "notes"];
    const esc = (v) => { if (v == null) return ""; const s = String(v).replace(/"/g, '""'); return /[",\n]/.test(s) ? `"${s}"` : s; };
    const rows = filtered.map((e) => cols.map((c) => esc(e[c])).join(","));
    const csv = "\uFEFF" + [cols.join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url;
    a.download = `A2_Bitacora_${new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-")}.csv`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success(`${filtered.length} eventos exportados`);
  };

  if (!canView) {
    return (
      <Layout title="Bitácora A2" subtitle="Acceso restringido">
        <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded" data-testid="bitacora-403">
          No tienes permiso para ver este panel (requiere <code>M32.a2.view</code>).
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="Bitácora A2" subtitle="Auto-agendamiento en tiempo real">
      <div className="space-y-4" data-testid="a2-bitacora-page">
        {/* Master switch al tope */}
        <A2AutopilotSwitch />

        {/* KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <KPI label="Eventos totales" value={kpis.total} icon={Bot} bg="bg-[#0A3747]" fg="text-white" testId="kpi-total" />
          <KPI label="Hoy" value={kpis.today} icon={CheckCircle2} bg="bg-emerald-500" fg="text-white" testId="kpi-today" />
          <KPI label="Técnicos activos hoy" value={kpis.techs_today} icon={User} bg="bg-blue-500" fg="text-white" testId="kpi-techs" />
          <KPI label="Última hora" value={kpis.last_hour} icon={Zap} bg="bg-amber-500" fg="text-white" testId="kpi-lasthour" />
        </div>

        {/* Toolbar */}
        <div className="bg-white border border-gray-200 rounded-lg p-3 flex items-center gap-2 flex-wrap">
          <div className="relative flex-1 min-w-[220px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Filtrar por técnico, OT, canal o nota…"
              className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-[#E0E621] focus:border-transparent"
              data-testid="bitacora-search"
            />
          </div>
          <label className="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer" data-testid="bitacora-autorefresh-label">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              className="accent-[#0A3747]"
              data-testid="bitacora-autorefresh"
            />
            Auto-refresh 15s
          </label>
          <button
            onClick={() => fetchEvents(false)}
            disabled={refreshing}
            className="flex items-center gap-1.5 px-3 py-2 rounded border border-gray-300 hover:bg-gray-50 text-sm disabled:opacity-40"
            data-testid="bitacora-refresh"
          >
            {refreshing ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />}
            Refrescar
          </button>
          <button
            onClick={exportCSV}
            className="flex items-center gap-1.5 px-3 py-2 rounded border border-gray-300 hover:bg-gray-50 text-sm"
            data-testid="bitacora-export"
          >
            <Download size={13} /> Exportar CSV
          </button>
          {lastFetchAt && (
            <span className="text-[10px] text-gray-400 ml-auto">
              actualizado {lastFetchAt.toLocaleTimeString("es-EC")}
            </span>
          )}
        </div>

        {/* Tabla */}
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-testid="a2-bitacora-table">
          {loading ? (
            <div className="p-8 text-center text-gray-400 flex items-center justify-center gap-2">
              <Loader2 size={16} className="animate-spin" /> Cargando eventos…
            </div>
          ) : filtered.length === 0 ? (
            <div className="p-8 text-center text-gray-500" data-testid="bitacora-empty">
              <Bot size={32} className="mx-auto text-gray-300 mb-2" />
              <div className="font-semibold text-sm">Aún no hay eventos</div>
              <div className="text-xs mt-1">
                Enciende el interruptor del motor A2 arriba y espera al próximo ciclo (cada 5 min).
              </div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-[10px] uppercase tracking-widest text-gray-500 font-semibold">
                  <tr>
                    <th className="text-left px-3 py-2">Fecha / hora</th>
                    <th className="text-left px-3 py-2">OT</th>
                    <th className="text-left px-3 py-2">Evento</th>
                    <th className="text-left px-3 py-2">Técnico</th>
                    <th className="text-left px-3 py-2">Slot</th>
                    <th className="text-left px-3 py-2">Canal</th>
                    <th className="text-left px-3 py-2">Nota</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((e, i) => (
                    <tr key={e.id || i} className="border-t border-gray-100 hover:bg-gray-50" data-testid={`bitacora-row-${i}`}>
                      <td className="px-3 py-2 font-mono text-[11px] text-gray-700 whitespace-nowrap">
                        <div className="flex items-center gap-1.5">
                          <Clock size={11} className="text-gray-400" />
                          {e.created_at ? new Date(e.created_at).toLocaleString("es-EC") : "—"}
                        </div>
                      </td>
                      <td className="px-3 py-2 font-mono text-xs text-[#0A3747]">
                        {e.odoo_task_id ? `#${e.odoo_task_id}` : "—"}
                      </td>
                      <td className="px-3 py-2">
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 font-semibold uppercase">
                          {e.event}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-xs">
                        {e.tech_email ? (
                          <div className="flex items-center gap-1.5">
                            <User size={11} className="text-gray-400" />
                            <span className="truncate max-w-[180px]" title={e.tech_email}>{e.tech_email}</span>
                          </div>
                        ) : "—"}
                      </td>
                      <td className="px-3 py-2 text-[11px] text-gray-600 whitespace-nowrap">
                        {e.slot_start ? new Date(e.slot_start).toLocaleString("es-EC", { timeZone: "America/Guayaquil", day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—"}
                        {e.slot_end && (
                          <> → {new Date(e.slot_end).toLocaleTimeString("es-EC", { timeZone: "America/Guayaquil", hour: "2-digit", minute: "2-digit" })}</>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        {e.channel ? (
                          <span className="text-[10px] px-2 py-0.5 rounded-full bg-blue-50 text-blue-800 font-mono">
                            <MessageSquare size={9} className="inline mr-1" />{e.channel}
                          </span>
                        ) : "—"}
                      </td>
                      <td className="px-3 py-2 text-xs text-gray-600 max-w-[280px] truncate" title={e.notes || ""}>
                        {e.notes || "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Footer info */}
        <div className="text-[10px] text-gray-400 italic">
          Los eventos se registran cada vez que el motor A2 auto-agenda una OT (loop cada 5 min o toggle manual).
          El canal <span className="font-mono">whatsapp+email</span> queda registrado pero la entrega real es un mock
          hasta la integración Twilio+Mailgun de Sprint 3.
        </div>
      </div>
    </Layout>
  );
}

function KPI({ label, value, icon: Icon, bg, fg, testId }) {
  return (
    <div className={`${bg} ${fg} rounded-lg p-3 flex items-center justify-between`} data-testid={testId}>
      <div>
        <div className="text-[10px] uppercase font-semibold opacity-80">{label}</div>
        <div className="text-2xl font-bold" style={{ fontFamily: "Space Grotesk" }}>{value}</div>
      </div>
      <Icon size={22} className="opacity-70" />
    </div>
  );
}
